import type { FC } from 'react';
import { Layer, Source } from 'react-map-gl';
import { activeLayerQuery } from '../../../core/mapV2/queries';
import { useAppSelector } from '../../../types';
import { useConfig } from '../../config';
import {
    ROAD_SECTIONS_LAYER_ID,
    ROAD_SECTIONS_MIN_ZOOM,
    ROAD_SECTIONS_SOURCE_ID,
    ROADS_SOURCE_ID,
    TILES_MAX_ZOOM,
    tilesUrl,
} from './sources';

const LINE_COLOR = '#1f2933';
const HOVER_COLOR = '#2f6fed';
const SELECTED_COLOR = '#e8590c';

const isSelected = ['boolean', ['feature-state', 'selected'], false];
// Hovered on the map, or in the list of retained sections of the multi-selection panel.
const isHovered = [
    'any',
    ['boolean', ['feature-state', 'hover'], false],
    ['boolean', ['feature-state', 'listHover'], false],
];

// Width at one zoom stop: normal, hovered, selected.
const widthAt = (normal: number, hovered: number, selected: number) => [
    'case',
    isSelected,
    selected,
    isHovered,
    hovered,
    normal,
];

// Référentiel layer: whole roads at low zoom, then every tronçon (one tile feature per RoadSection,
// identified by `id`, which `promoteId` makes the feature id so feature-state can target it).
// Attributes are not in the tiles: they come from the loaded sections and are applied as feature-state.
export const ReferenceLayers: FC = () => {
    const { backendUrl } = useConfig();
    const activeLayer = useAppSelector(activeLayerQuery);
    const visibility = activeLayer === 'reference' ? 'visible' : 'none';

    return (
        <>
            <Source
                id={ROADS_SOURCE_ID}
                type="vector"
                tiles={[tilesUrl(backendUrl!, ROADS_SOURCE_ID)]}
                minzoom={0}
                maxzoom={TILES_MAX_ZOOM}
                promoteId="id"
            />
            <Source
                id={ROAD_SECTIONS_SOURCE_ID}
                type="vector"
                tiles={[tilesUrl(backendUrl!, ROAD_SECTIONS_SOURCE_ID)]}
                minzoom={ROAD_SECTIONS_MIN_ZOOM}
                maxzoom={TILES_MAX_ZOOM}
                promoteId="id"
            />
            <Layer
                id="map-v2-roads"
                source={ROADS_SOURCE_ID}
                source-layer={ROADS_SOURCE_ID}
                type="line"
                maxzoom={ROAD_SECTIONS_MIN_ZOOM}
                layout={{ visibility, 'line-cap': 'round', 'line-join': 'round' }}
                paint={{ 'line-color': LINE_COLOR, 'line-width': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 2] }}
            />
            <Layer
                id={ROAD_SECTIONS_LAYER_ID}
                source={ROAD_SECTIONS_SOURCE_ID}
                source-layer={ROAD_SECTIONS_SOURCE_ID}
                type="line"
                minzoom={ROAD_SECTIONS_MIN_ZOOM}
                layout={{ visibility, 'line-cap': 'butt', 'line-join': 'round' }}
                paint={{
                    // Sections the filters leave out stay on the map but faded (a selected one stays plain).
                    'line-opacity': ['case', isSelected, 1, ['boolean', ['feature-state', 'dim'], false], 0.12, 1],
                    // The indicator color is a feature-state string set by useReferenceColoring. `typeof` guards
                    // the sections that have none (not colored yet, or indicator "–").
                    'line-color': [
                        'case',
                        isSelected,
                        SELECTED_COLOR,
                        isHovered,
                        HOVER_COLOR,
                        ['==', ['typeof', ['feature-state', 'color']], 'string'],
                        ['feature-state', 'color'],
                        LINE_COLOR,
                    ],
                    'line-width': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        9,
                        widthAt(2, 4, 5),
                        14,
                        widthAt(4, 7, 9),
                        18,
                        widthAt(8, 12, 14),
                    ],
                }}
            />
        </>
    );
};
